import { ReactNode } from 'react'
import cx from 'classnames'
import Styles from '../ReviewsWizard/ReviewTemplates/ReviewTemplates.module.scss'
import { ReactComponent as PlusIcon } from 'assets/icons/plus16.svg'
import { collectResults } from 'utils/query'
import { ReviewTemplate } from 'types'
import { ReviewTemplateRow } from 'components/ReviewsWizard/ReviewTemplates/ReviewTemplateRow'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { ColdState } from 'components/ReviewsWizard/ReviewTemplates/ColdState'
import { Button } from 'components/Button/Button'
import { useNewReviewTemplateModalProps } from 'components/ReviewTemplateFormModal/useNewReviewTemplateModalProps'
import { useReviewTemplates } from 'hooks/queries/reviewTemplates/useReviewTemplates'
import { NewReviewTemplateFormModal } from 'components/ReviewTemplateFormModal/NewReviewTemplateFormModal'
import { ReviewTemplatesSkeleton } from 'components/ReviewsWizard/ReviewTemplates/ReviewTemplatesSkeleton'
import { Modal } from 'almanac-ui/Modal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useModals } from 'almanac-ui/Modal'
import { StackModalProps } from 'almanac-ui/Modal/Types'

interface Props {
  workspaceId: number
  header?: ReactNode
  onSelect?: (template: ReviewTemplate) => void
}

export const NewReviewTemplatesModalContent = ({ workspaceId, header, onSelect }: Props) => {
  const queryResult = useReviewTemplates({ workspaceId })
  const templates = collectResults(queryResult, (data) => data.review_templates)

  const renderContent = () => {
    if (queryResult.isLoading) return <ReviewTemplatesSkeleton />
    if (templates.length === 0) return <ColdState />

    return (
      <>
        <div className={cx(Styles.headers, Styles.withLastEditor)}>
          <div>Review templates</div>
          <div>Last edited by</div>
          <div>Used</div>
        </div>
        {templates.map((template) => (
          <ReviewTemplateRow key={template.id} template={template} onSelect={onSelect} showLastEditor />
        ))}
      </>
    )
  }

  return (
    <>
      {header}
      <div className={Styles.container}>
        {renderContent()}
        <InfiniteScroll hasMore={queryResult.hasNextPage ?? false} loadMore={queryResult.fetchNextPage} />
      </div>
    </>
  )
}

export const NewReviewTemplatesModal = ({ index }: StackModalProps) => {
  const { onSubmit } = useNewReviewTemplateModalProps()
  const workspace = useCurrentWorkspace()
  const { openModal } = useModals()

  const openReviewTemplateFormModal = () =>
    openModal(NewReviewTemplateFormModal, {
      onSubmit,
    })

  if (!workspace) return null

  return (
    <Modal.Root size="lg">
      {index > 0 && (
        <Modal.Top>
          <Modal.BackButton />
        </Modal.Top>
      )}
      <Modal.Header
        title="All Templates"
        actions={
          <Button size="md" variant="tertiary" frontIcon={PlusIcon} onPress={openReviewTemplateFormModal}>
            New Template
          </Button>
        }
      />
      <Modal.Content>
        <NewReviewTemplatesModalContent workspaceId={workspace.id} />
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent />
      </Modal.Footer>
    </Modal.Root>
  )
}
